import {focusTrap, FocusTrap} from './util/focus-trap';
import {ScrollBlock} from './util/ScrollBlock';

type ModalConfig = {
    rootSelector: string;
    overlaySelector: string;
    contentSelector: string;
    closeSelector: string;
    titleSelector: string;
    bodySelector: string;
};

const CLASS_ENABLED: string = 'enabled';
const CLASS_OPEN: string = 'open';
const CLASS_ANCHORED: string = 'anchored';
const CLASS_SCOPE: string = 's-wordpress';

const KEY_ESCAPE: string = 'Escape';

export class Modal {
    private static _instance: Modal | undefined;

    private _isOpen: boolean = false;
    private _interval: number | undefined;
    private readonly _body: HTMLElement | null;
    private readonly _close: HTMLElement | null;
    private readonly _content: HTMLElement | null;
    private readonly _focusTrap: FocusTrap | undefined;
    private readonly _overlay: HTMLElement | null;
    private readonly _root: HTMLElement | null;
    private readonly _title: HTMLElement | null;

    private constructor(config: ModalConfig) {
        this._root = document.querySelector(config.rootSelector);
        this._overlay = document.querySelector(config.overlaySelector);
        this._content = document.querySelector(config.contentSelector);
        this._close = document.querySelector(config.closeSelector);
        this._title = document.querySelector(config.titleSelector);
        this._body = document.querySelector(config.bodySelector);

        if (this._content !== null) {
            this._focusTrap = focusTrap(this._content);
        }

        this.addEventHandlers();
    }

    private _theme: string = '';

    private set theme(theme: string) {
        if (this._root === null) {
            return;
        }

        if (this._theme !== '') {
            this._root.classList.remove(this._theme);
        }

        if (theme !== '') {
            this._root.classList.add(theme);
        }

        this._theme = theme;
    }

    private set disableWPStyles(disableWPStyles: boolean | undefined) {
        if (this._body === null) {
            return;
        }

        if (disableWPStyles === undefined || disableWPStyles === false) {
            this._body.classList.add(CLASS_SCOPE);

            return;
        }

        this._body.classList.remove(CLASS_SCOPE);
    }

    private set title(title: string) {
        if (this._title === null) {
            return;
        }

        this._title.innerText = title;
    }

    private set body(body: string | HTMLElement) {
        if (this._body === null) {
            return;
        }

        if (typeof body === 'string') {
            this._body.classList.add(CLASS_SCOPE);
            // tslint:disable-next-line no-inner-html
            this._body.innerHTML = body;

            return;
        }

        // tslint:disable-next-line no-inner-html
        this._body.innerHTML = '';
        this._body.classList.remove(CLASS_SCOPE);
        this._body.appendChild(body);
    }

    private set anchor(anchor: HTMLElement | undefined) {
        if (this._root === null || this._content === null) {
            return;
        }

        if (anchor === undefined) {
            this._root.classList.remove(CLASS_ANCHORED);
            this._content.style.right = '';
            this._content.style.top = '';

            return;
        }

        const rect: ClientRect = anchor.getBoundingClientRect();
        const windowWidth: number = document.body.clientWidth;
        const right: number = windowWidth - (rect.width + rect.left);
        const top: number = rect.top;

        this._root.classList.add(CLASS_ANCHORED);
        this._content.style.right = `${right}px`;
        this._content.style.top = `${top}px`;
    }

    // tslint:disable-next-line function-name
    public static config(config: ModalConfig): void {
        this._instance = new Modal(config);
    }

    // tslint:disable-next-line function-name
    public static open(
        title: string,
        body: string | HTMLElement,
        theme: string = '',
        anchor?: HTMLElement | undefined,
        disableWPStyles?: boolean | undefined,
    ): void {
        if (this._instance === undefined) {
            throw new Error('Please configure the modal before usage');
        }

        this._instance.title = title;
        this._instance.body = body;
        this._instance.theme = theme;
        this._instance.anchor = anchor;
        this._instance.disableWPStyles = disableWPStyles;
        this._instance.show();
    }

    // tslint:disable-next-line function-name
    public static close(): void {
        if (this._instance === undefined) {
            return;
        }

        this._instance.hide();
    }

    private addEventHandlers(): void {
        if (this._close !== null) {
            this._close.addEventListener('click', (): void => {
                this.hide();
            });
        }

        if (this._overlay !== null) {
            this._overlay.addEventListener('click', (): void => {
                this.hide();
            });
        }

        window.addEventListener('keydown', (event: KeyboardEvent): void => {
            if (!this._isOpen) {
                return;
            }

            if (event.key === KEY_ESCAPE) {
                this.hide();
            }
        });
    }

    private show(): void {
        if (this._root === null) {
            return;
        }

        ScrollBlock.enable();
        this._root.classList.add(CLASS_ENABLED);
        this._isOpen = true;

        window.clearTimeout(this._interval);
        this._interval = window.setTimeout((): void => {
            this._root?.classList.add(CLASS_OPEN);
        }, 50);

        if (this._focusTrap) {
            this._focusTrap.enable();
        }
    }

    private hide(): void {
        if (this._root === null) {
            return;
        }

        if (this._focusTrap) {
            this._focusTrap.disable();
        }

        ScrollBlock.disable();
        this._root.classList.remove(CLASS_OPEN);
        this._isOpen = false;

        window.clearTimeout(this._interval);
        this._interval = window.setTimeout((): void => {
            this._root?.classList.remove(CLASS_ENABLED);
        }, 50);
    }
}
